/**
 * InventoryView (E4b Item 5) — the VIEW-ONLY inventory, linked from both
 * contractor documents. No editing, no submission, no actions of any kind.
 *
 * DEGRADES BY DESIGN, because P5 fills the payload in one piece at a time:
 *   - no rooms   -> renders a flat list (everything arrives in `unroomed`)
 *   - no cube    -> OMITS the cu ft / weight tiles rather than printing 0
 *   - no media   -> the photos/video section simply isn't there
 *   - nothing    -> an honest empty state, not a broken-looking blank page
 *
 * The shape is documented in routes/portal.ts (inventoryDetail). P5 should
 * populate `media` and `room_key` and conform to it rather than introducing a
 * second shape.
 */

import React from 'react';
import { formatDate } from '../../lib/utils';

const METHOD_LABEL: Record<string, string> = {
  photos: 'Photos',
  video: 'Video',
  text_list: 'Written list',
  cube_sheet: 'Full list',
  room_by_room: 'Room by room',
};

function ItemRows({ items }: { items: any[] }) {
  return (
    <table className="pp-tbl">
      <tbody>
        {items.map((it: any, i: number) => (
          <tr key={i}>
            <td>
              {it.name}
              {it.notes && <div className="pp-rule">{it.notes}</div>}
            </td>
            <td className="pp-qty">×{Number(it.quantity).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// No Download PDF here: an inventory link has no document behind it, so the
// PDF route answers "not found". pdfPath stays in the props for when it does.
export function InventoryView({ data }: { data: any; pdfPath?: string }) {
  const inv = data.inventory ?? {};
  const job = data.job ?? {};
  const totals = inv.totals ?? {};
  const rooms: any[] = inv.rooms ?? [];
  const unroomed: any[] = inv.unroomed ?? [];
  const media: any[] = inv.media ?? [];

  return (
    <>
      <div className="pp-head">
        <div>
          <div className="pp-kicker">Baltic Van Lines</div>
          <h1 className="pp-h1">Inventory</h1>
          <div className="pp-jobno">#{job.number}</div>
        </div>
        <span className="pp-badge">View only</span>
      </div>

      {inv.status !== 'ready' ? (
        <div className="pp-card">
          <div className="pp-sec">Nothing recorded yet</div>
          <p className="pp-sub" style={{ margin: 0 }}>
            No inventory has been captured for this job. The office will add it as soon as the client provides their list.
          </p>
        </div>
      ) : (
        <>
          {/* Totals — a tile appears only when there is a real figure behind it. */}
          <div className="pp-card">
            <div className="pp-sec">Summary</div>
            <div className="pp-tiles">
              <div className="pp-tile">
                <div className="pp-tile-l">Items</div>
                <div className="pp-tile-v">{Number(totals.itemCount || 0).toLocaleString()}</div>
              </div>
              {totals.cuft != null && (
                <div className="pp-tile">
                  <div className="pp-tile-l">Volume</div>
                  <div className="pp-tile-v">{Number(totals.cuft).toLocaleString()} cu ft</div>
                </div>
              )}
              {totals.weight != null && (
                <div className="pp-tile">
                  <div className="pp-tile-l">Weight</div>
                  <div className="pp-tile-v">{Number(totals.weight).toLocaleString()} lb</div>
                </div>
              )}
            </div>
            {(inv.method || inv.submittedAt) && (
              <p className="pp-sub" style={{ marginTop: 10, marginBottom: 0 }}>
                {inv.method ? METHOD_LABEL[inv.method] ?? inv.method : 'Recorded'}
                {inv.submittedAt ? ` · submitted ${formatDate(String(inv.submittedAt).slice(0, 10))}` : ''}
              </p>
            )}
          </div>

          {/* Room-by-room when rooms exist; otherwise the flat list below. */}
          {rooms.length > 0 && (
            <div className="pp-card">
              <div className="pp-sec">By room</div>
              {rooms.map((r) => (
                <div key={r.key} className="pp-room">
                  <div className="pp-room-h">
                    <span className="pp-room-n">{r.name}</span>
                    <span className="pp-room-m">
                      {Number(r.itemCount).toLocaleString()} items{r.cuft ? ` · ${Number(r.cuft).toLocaleString()} cu ft` : ''}
                    </span>
                  </div>
                  <div className="pp-room-b"><ItemRows items={r.items} /></div>
                </div>
              ))}
            </div>
          )}

          {unroomed.length > 0 && (
            <div className="pp-card">
              <div className="pp-sec">{rooms.length > 0 ? 'Other items' : 'Items'}</div>
              <div className="pp-wrap"><ItemRows items={unroomed} /></div>
            </div>
          )}

          {media.length > 0 && (
            <div className="pp-card">
              <div className="pp-sec">Photos &amp; video</div>
              <div className="pp-tiles">
                {media.map((m: any, i: number) => (
                  <a key={i} className="pp-tile" href={m.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>
                    <div className="pp-tile-l">{m.kind === 'video' ? 'Video' : 'Photo'}</div>
                    <div className="pp-tile-v sm">{m.caption || `Open ${m.kind === 'video' ? 'video' : 'photo'}`}</div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {inv.notes && (
            <div className="pp-card">
              <div className="pp-sec">Notes</div>
              <div className="pp-sub" style={{ whiteSpace: 'pre-wrap' }}>{inv.notes}</div>
            </div>
          )}
        </>
      )}
    </>
  );
}
